import React, { useState } from 'react';

import { type TabOption, Tabs } from '@/components/chariot-ui';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { CustomerData } from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';

import { ClientBudgetTab } from './ClientBudgetTab';
import { ClientJobsTab } from './ClientJobsTab';
import { ClientRunTab } from './ClientRunTab';
import { usePiusEnabled } from './usePiusEnabled';

interface CustomerDiscoveryTabProps {
  customer: CustomerData;
}

const discoverySubTabs: TabOption[] = [
  { label: 'Run', value: 'run' },
  { label: 'Budget / Limits', value: 'budget' },
  { label: 'Jobs', value: 'jobs' },
];

export const CustomerDiscoveryTab: React.FC<CustomerDiscoveryTabProps> = ({
  customer,
}) => {
  const { userType } = useAuth();
  const [selectedSubTab, setSelectedSubTab] = useState<TabOption>(
    discoverySubTabs[0]
  );

  const { isPiusEnabled, isLoading, isEnabling, handleEnable } = usePiusEnabled(
    customer.email
  );

  // Defense-in-depth: Praetorian-only tab (Layer 3)
  if (userType !== 'praetorian') return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!isPiusEnabled) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-chariot-text-secondary text-sm">
          Pius Discovery is not enabled for this customer.
        </p>
        <Button
          onClick={handleEnable}
          disabled={isEnabling || isLoading}
          size="sm"
        >
          {isEnabling ? 'Enabling...' : 'Enable Pius Discovery'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Tabs
        tabs={discoverySubTabs}
        selected={selectedSubTab}
        onSelect={setSelectedSubTab}
        variant="primary"
      />
      <div className="flex-1 overflow-y-auto">
        {selectedSubTab.value === 'run' && (
          <ClientRunTab client={customer.email} />
        )}
        {selectedSubTab.value === 'budget' && (
          <ClientBudgetTab client={customer.email} />
        )}
        {selectedSubTab.value === 'jobs' && (
          <ClientJobsTab client={customer.email} />
        )}
      </div>
    </div>
  );
};
